import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { Placement } from '~/src/components/generic/TooltipV2/types'
import { canAcceptRef } from '~/src/components/generic/TooltipV2/helper'

import { tw } from '~/lib/utils'

type Props = {
  title: ReactNode
  children: ReactElement
  placement?: Placement
  offset?: number
  enterDelay?: number
  leaveDelay?: number
  disabled?: boolean
  className?: string
  onOpen?: () => void
}

const TooltipV2 = ({
  title,
  children,
  placement = 'top',
  offset = 8,
  enterDelay = 400,
  leaveDelay = 100,
  disabled = false,
  className,
  onOpen,
}: Props) => {
  const tooltipId = useId()

  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  const enterTime = useRef<number | null>(null)
  const leaveTime = useRef<number | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  const tipRef = useRef<HTMLDivElement | null>(null)

  const clearTimers = () => {
    if (enterTime.current) window.clearTimeout(enterTime.current)
    if (leaveTime.current) window.clearTimeout(leaveTime.current)

    enterTime.current = null
    leaveTime.current = null
  }

  const updatePosition = () => {
    const anchor = anchorRef.current
    const tip = tipRef.current

    if (!anchor || !tip) return

    const anchorRect = anchor.getBoundingClientRect()
    const tipRect = tip.getBoundingClientRect()

    const scrollX = window.scrollX || document.documentElement.scrollLeft
    const scrollY = window.scrollY || document.documentElement.scrollTop

    let top = 0
    let left = 0

    switch (placement) {
      case 'top':
        top = anchorRect.top - tipRect.height - offset
        left = anchorRect.left + (anchorRect.width - tipRect.width) / 2
        break
      case 'bottom':
        top = anchorRect.bottom + offset
        left = anchorRect.left + (anchorRect.width - tipRect.width) / 2
        break
      case 'left':
        top = anchorRect.top + (anchorRect.height - tipRect.height) / 2
        left = anchorRect.left - tipRect.width - offset
        break
      case 'right':
        top = anchorRect.top + (anchorRect.height - tipRect.height) / 2
        left = anchorRect.right + offset
        break
    }

    // Viewport clamp
    const pad = 8
    const vw = window.innerWidth
    const vh = window.innerHeight

    left = Math.min(Math.max(left, pad), vw - tipRect.width - pad)
    top = Math.min(Math.max(top, pad), vh - tipRect.height - pad)

    setPosition({ top: top + scrollY, left: left + scrollX })
  }

  const show = () => {
    if (disabled) return

    clearTimers()

    enterTime.current = window.setTimeout(() => {
      setOpen(true)
      onOpen?.()
    }, enterDelay)
  }

  const hide = () => {
    clearTimers()

    leaveTime.current = window.setTimeout(() => setOpen(false), leaveDelay)
  }

  useEffect(() => {
    if (!open) return

    const raf = requestAnimationFrame(updatePosition)
    const onScroll = () => updatePosition()
    const onResize = () => updatePosition()

    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement, offset])

  useEffect(() => () => clearTimers(), [])

  const child = useMemo(() => {
    const setRef = (node: HTMLElement | null) => {
      anchorRef.current = node
    }

    const attach = {
      onMouseEnter: (event: React.MouseEvent) => {
        children.props.onMouseEnter?.(event)
        show()
      },
      onMouseLeave: (event: React.MouseEvent) => {
        children.props.onMouseLeave?.(event)
        hide()
      },
      onFocus: (event: React.FocusEvent) => {
        children.props.onFocus?.(event)
        show()
      },
      onBlur: (event: React.FocusEvent) => {
        children.props.onBlur?.(event)
        hide()
      },
      'aria-describedby': open ? tooltipId : undefined,
    }

    const childIsDisabled = !!children.props.disabled

    //  no wrapper for DOM elements / forwardRef components (unless child is disabled —
    //  disabled elements don't fire mouse events so the wrapper must capture hover instead)
    if (canAcceptRef(children) && !childIsDisabled) {
      return React.cloneElement(children, {
        ...attach,
        ref: setRef,
      })
    }

    // Fallback: wrapper contains child (not sibling).
    // Use a block-level div so it fills the container width and doesn't
    // interfere with layouts like Headless UI's MenuItem.
    return (
      <div
        ref={setRef}
        onBlurCapture={() => hide()}
        onFocusCapture={() => show()}
        onMouseEnter={() => show()}
        onMouseLeave={() => hide()}
      >
        {React.cloneElement(children, {
          'aria-describedby': open ? tooltipId : undefined,
        })}
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, open, tooltipId])

  const tooltipNode =
    open && title !== null
      ? createPortal(
          <div
            className={tw(
              'pointer-events-none fixed z-[9999] select-none rounded',
              'bg-gray-700 p-2 text-body-s leading-tight text-white shadow-md',
              className ?? ''
            )}
            id={tooltipId}
            ref={(node) => {
              tipRef.current = node
            }}
            role="tooltip"
            style={{
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
            }}
          >
            {title}
          </div>,
          document.body
        )
      : null

  return (
    <>
      {child}
      {tooltipNode}
    </>
  )
}

TooltipV2.displayName = 'Tooltip'

export default TooltipV2
